import { useEffect, useRef, useState } from 'preact/hooks';
import { useOddball } from '../midi-context';
import { getAudioContext, createNoiseBuffer } from '../audio/audio-engine';
import { useCCValues } from '../hooks/use-oddball-events';
import type { TapEvent, ShakeEvent, TwistEvent, CCUpdateEvent } from '../../src/types';
import { OddballCC } from '../../src/types';

interface DrumEngine {
  ctx: AudioContext;
  masterGain: GainNode;
  noiseBuffer: AudioBuffer;
  metronomeInterval: number;
  metronomeOsc: OscillatorNode | null;
  speedMul: number;
}

function createDrumEngine(): DrumEngine {
  const ctx = getAudioContext();
  const masterGain = ctx.createGain();
  masterGain.gain.value = 0.6;
  masterGain.connect(ctx.destination);

  return {
    ctx, masterGain,
    noiseBuffer: createNoiseBuffer(ctx, 0.3),
    metronomeInterval: 0,
    metronomeOsc: null,
    speedMul: 1,
  };
}

function playKick(eng: DrumEngine, velocity: number) {
  const { ctx, masterGain } = eng;
  const t = ctx.currentTime;
  const vol = (velocity / 127) * 0.5;
  const pitchScale = 0.7 + (velocity / 127) * 0.6;

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(150 * pitchScale, t);
  osc.frequency.exponentialRampToValueAtTime(40, t + 0.1);

  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);

  osc.connect(g).connect(masterGain);
  osc.start(t);
  osc.stop(t + 0.35);
}

function playHiHat(eng: DrumEngine, velocity: number) {
  const { ctx, masterGain, noiseBuffer } = eng;
  const t = ctx.currentTime;
  const vol = (velocity / 127) * 0.3;
  const isOpen = velocity > 80;
  const duration = isOpen ? 0.15 : 0.04;

  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer;

  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = isOpen ? 6000 : 8000;

  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 10000;
  bp.Q.value = 1;

  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + 0.002);
  g.gain.exponentialRampToValueAtTime(0.001, t + duration);

  src.connect(hp).connect(bp).connect(g).connect(masterGain);
  src.start(t);
  src.stop(t + duration + 0.01);
}

function playSnare(eng: DrumEngine, velocity: number) {
  const { ctx, masterGain, noiseBuffer } = eng;
  const t = ctx.currentTime;
  const vol = (velocity / 127) * 0.35;
  const snappiness = 0.5 + (velocity / 127) * 0.5;

  // Noise body
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer;
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'bandpass';
  noiseFilter.frequency.value = 3000 + snappiness * 2000;
  noiseFilter.Q.value = 0.8;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0, t);
  noiseGain.gain.linearRampToValueAtTime(vol * 0.7, t + 0.003);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.1 + snappiness * 0.08);
  src.connect(noiseFilter).connect(noiseGain).connect(masterGain);
  src.start(t);
  src.stop(t + 0.25);

  // Sine body
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(180, t);
  osc.frequency.exponentialRampToValueAtTime(120, t + 0.05);
  const oscGain = ctx.createGain();
  oscGain.gain.setValueAtTime(0, t);
  oscGain.gain.linearRampToValueAtTime(vol * 0.4, t + 0.003);
  oscGain.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
  osc.connect(oscGain).connect(masterGain);
  osc.start(t);
  osc.stop(t + 0.1);
}

function playClick(eng: DrumEngine) {
  const { ctx, masterGain } = eng;
  const t = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.value = 1000;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.04, t + 0.001);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.02);
  osc.connect(g).connect(masterGain);
  osc.start(t);
  osc.stop(t + 0.03);
}

type PadType = 'kick' | 'hat' | 'snare';

export function RhythmMapper() {
  const { parser, connected, speedModifier } = useOddball();
  const cc = useCCValues();
  const engineRef = useRef<DrumEngine | null>(null);
  const speedRef = useRef(1.0);
  const [lastHit, setLastHit] = useState<{ pad: PadType; velocity: number; time: number } | null>(null);
  const [bpm, setBpm] = useState(0);

  useEffect(() => { speedRef.current = speedModifier; }, [speedModifier]);
  useEffect(() => {
    const eng = engineRef.current;
    if (!eng) return;
    if (connected) {
      eng.masterGain.gain.setTargetAtTime(0.6, eng.ctx.currentTime, 0.05);
    } else {
      clearInterval(eng.metronomeInterval);
      eng.metronomeInterval = 0;
      eng.masterGain.gain.setTargetAtTime(0, eng.ctx.currentTime, 0.05);
    }
  }, [connected]);

  useEffect(() => {
    const eng = createDrumEngine();
    engineRef.current = eng;

    const onTap = (evt: TapEvent) => {
      playKick(eng, evt.velocity);
      setLastHit({ pad: 'kick', velocity: evt.velocity, time: performance.now() });
    };
    const onShake = (evt: ShakeEvent) => {
      playHiHat(eng, evt.velocity);
      setLastHit({ pad: 'hat', velocity: evt.velocity, time: performance.now() });
    };
    const onTwist = (evt: TwistEvent) => {
      playSnare(eng, evt.velocity);
      setLastHit({ pad: 'snare', velocity: evt.velocity, time: performance.now() });
    };

    const onCC = (evt: CCUpdateEvent) => {
      eng.speedMul = speedRef.current;
      if (evt.cc === OddballCC.SpinSmoothed) {
        // Spin → metronome tempo, scaled by speed modifier
        const tempo = Math.round(40 + (evt.value / 127) * 160); // 40-200 BPM
        setBpm(tempo);

        clearInterval(eng.metronomeInterval);
        if (evt.value > 5) {
          const intervalMs = 60000 / tempo / eng.speedMul;
          eng.metronomeInterval = window.setInterval(() => playClick(eng), intervalMs);
        }
      }
    };

    parser.on('tap', onTap);
    parser.on('shake', onShake);
    parser.on('twist', onTwist);
    parser.on('cc', onCC);

    return () => {
      parser.off('tap', onTap);
      parser.off('shake', onShake);
      parser.off('twist', onTwist);
      parser.off('cc', onCC);
      clearInterval(eng.metronomeInterval);
      eng.masterGain.disconnect();
    };
  }, [parser]);

  const padDefs: { id: PadType; label: string; color: string }[] = [
    { id: 'kick', label: 'KICK', color: '#ffb432' },
    { id: 'hat', label: 'HAT', color: '#508cff' },
    { id: 'snare', label: 'SNARE', color: '#b450ff' },
  ];

  const now = performance.now();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', paddingTop: '30px' }}>
      <h2 style={{ fontSize: '1rem', fontWeight: 300, color: '#888' }}>Rhythm Mapper</h2>

      {/* Pads */}
      <div style={{ display: 'flex', gap: '16px' }}>
        {padDefs.map(({ id, label, color }) => {
          const isActive = lastHit && lastHit.pad === id && now - lastHit.time < 200;
          return (
            <div
              key={id}
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isActive ? color : '#1a1a1a',
                color: isActive ? '#111' : '#555',
                fontSize: '0.8rem',
                fontWeight: 700,
                transition: 'all 0.08s',
                boxShadow: isActive ? `0 0 20px ${color}44` : 'none',
              }}
            >
              {label}
            </div>
          );
        })}
      </div>

      {/* Velocity meter */}
      <div style={{ width: '260px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#555', marginBottom: '4px' }}>
          <span>Velocity</span>
          <span>{lastHit ? lastHit.velocity : 0}</span>
        </div>
        <div style={{ height: '6px', background: '#1a1a1a', borderRadius: '3px', overflow: 'hidden' }}>
          <div
            style={{
              width: lastHit ? (lastHit.velocity / 127) * 100 + '%' : '0%',
              height: '100%',
              background: lastHit ? padDefs.find((p) => p.id === lastHit.pad)?.color : '#555',
              borderRadius: '3px',
              transition: 'width 0.05s',
            }}
          />
        </div>
      </div>

      {/* BPM */}
      <div style={{ fontSize: '0.85rem', color: '#666', fontFamily: 'monospace' }}>
        {bpm > 0 ? `${bpm} BPM` : 'Spin for metronome'}
      </div>

      <div style={{ fontSize: '0.7rem', color: '#444', maxWidth: '300px', textAlign: 'center', lineHeight: 1.6 }}>
        Tap = kick &middot; Shake = hi-hat &middot; Twist = snare &middot; Spin = metronome tempo
      </div>
    </div>
  );
}
